'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslation } from '@/hooks/useTranslation';
import { useAuthGuard } from '@/hooks/useAuthGuard';
import { useConfirm } from '@/components/common/feedback/ConfirmProvider';
import { toast } from '@/lib/toast';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, RefreshCw, ArrowLeft, Ban } from 'lucide-react';
import { knowledgeBaseApi, type SyncTaskListItem } from '../_lib/api';

const statusVariantMap: Record<SyncTaskListItem['status'], 'default' | 'secondary' | 'destructive' | 'outline'> = {
  PENDING: 'outline',
  SYNCING: 'secondary',
  COMPLETED: 'default',
  FAILED: 'destructive',
};

export default function KnowledgeBaseSyncTasksPage() {
  const { t } = useTranslation();
  const { isReady } = useAuthGuard();
  const router = useRouter();
  const confirm = useConfirm();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tasks, setTasks] = useState<SyncTaskListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [lastCheckedAt, setLastCheckedAt] = useState<Date | null>(null);
  const [terminatingTaskId, setTerminatingTaskId] = useState<string | null>(null);

  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await knowledgeBaseApi.getSyncTasks({ limit: 20, offset: 0 });
      setTasks(response.items);
      setTotal(response.total);
      setLastCheckedAt(new Date());
    } catch (err) {
      console.error('Failed to fetch sync tasks:', err);
      setError(t.knowledgeBase.syncTaskList.loadFailed);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (!isReady) return;
    fetchTasks();
  }, [isReady, fetchTasks]);

  const handleTerminate = useCallback(
    async (task: SyncTaskListItem) => {
      const confirmed = await confirm({
        title: t.knowledgeBase.syncTaskList.terminateConfirmTitle,
        description: t.knowledgeBase.syncTaskList.terminateConfirmDescription.replace(
          '{taskId}',
          task.id,
        ),
        confirmText: t.knowledgeBase.syncTaskList.terminateConfirmAction,
      });
      if (!confirmed) return;
      try {
        setTerminatingTaskId(task.id);
        await knowledgeBaseApi.terminateSyncTask(task.id);
        toast.success(t.knowledgeBase.syncTaskList.terminateSuccess);
        await fetchTasks();
      } catch (err) {
        console.error('Failed to terminate sync task:', err);
        toast.error(t.knowledgeBase.syncTaskList.terminateFailed);
      } finally {
        setTerminatingTaskId(null);
      }
    },
    [confirm, fetchTasks, t],
  );

  const rows = useMemo(() => tasks, [tasks]);

  if (!isReady) {
    return (
      <div className="flex h-full items-center justify-center bg-[#f4f5f7]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-[#f4f5f7]">
      <div className="flex-shrink-0 border-b border-gray-200 bg-white px-6 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5"
              onClick={() => router.push('/knowledge-base')}
            >
              <ArrowLeft className="h-4 w-4" />
              {t.knowledgeBase.syncTaskList.back}
            </Button>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">
                {t.knowledgeBase.syncTaskList.title}
              </h1>
              <p className="text-sm text-gray-500">
                {t.knowledgeBase.syncTaskList.subtitle}
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={fetchTasks}
            disabled={loading}
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            {t.knowledgeBase.syncTaskList.refresh}
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-6">
        <Card className="border-gray-200 shadow-sm">
          <CardHeader className="space-y-2">
            <CardTitle className="text-base font-semibold text-gray-900">
              {t.knowledgeBase.syncTaskList.overviewTitle}
            </CardTitle>
            <p className="text-sm text-gray-500">
              {t.knowledgeBase.syncTaskList.totalLabel.replace('{count}', String(total))}
              {lastCheckedAt
                ? ` · ${t.knowledgeBase.syncTaskList.lastChecked.replace('{time}', lastCheckedAt.toLocaleString())}`
                : ''}
            </p>
          </CardHeader>
          <CardContent>
            {loading && (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t.knowledgeBase.syncTaskList.loading}
              </div>
            )}

            {!loading && error && (
              <div className="text-sm text-red-600">{error}</div>
            )}

            {!loading && !error && rows.length === 0 && (
              <div className="text-sm text-gray-500">{t.knowledgeBase.syncTaskList.empty}</div>
            )}

            {!loading && rows.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 bg-[#fafafa] text-xs text-gray-600">
                      <th className="px-4 py-3 font-medium">{t.knowledgeBase.syncTaskList.table.taskId}</th>
                      <th className="px-4 py-3 font-medium">{t.knowledgeBase.syncTaskList.table.status}</th>
                      <th className="px-4 py-3 font-medium">{t.knowledgeBase.syncTaskList.table.triggerSource}</th>
                      <th className="px-4 py-3 font-medium">{t.knowledgeBase.syncTaskList.table.total}</th>
                      <th className="px-4 py-3 font-medium">{t.knowledgeBase.syncTaskList.table.processed}</th>
                      <th className="px-4 py-3 font-medium">{t.knowledgeBase.syncTaskList.table.processing}</th>
                      <th className="px-4 py-3 font-medium">{t.knowledgeBase.syncTaskList.table.failed}</th>
                      <th className="px-4 py-3 font-medium">{t.knowledgeBase.syncTaskList.table.skipped}</th>
                      <th className="px-4 py-3 font-medium">{t.knowledgeBase.syncTaskList.table.tokens}</th>
                      <th className="px-4 py-3 font-medium">{t.knowledgeBase.syncTaskList.table.chunks}</th>
                      <th className="px-4 py-3 font-medium">{t.knowledgeBase.syncTaskList.table.createdAt}</th>
                      <th className="px-4 py-3 font-medium">{t.knowledgeBase.syncTaskList.table.action}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((task) => (
                      <tr
                        key={task.id}
                        className="border-b border-gray-100 hover:bg-gray-50"
                      >
                        <td className="px-4 py-3 font-medium text-gray-900 break-all">
                          {task.id}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={statusVariantMap[task.status]}>
                            {t.knowledgeBase.syncTaskList.status[task.status]}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          {t.knowledgeBase.syncTaskList.triggerSource[task.triggerSource ?? 'UNKNOWN']}
                        </td>
                        <td className="px-4 py-3 text-gray-700">{task.totalItems ?? '-'}</td>
                        <td className="px-4 py-3 text-gray-700">{task.processedItems}</td>
                        <td className="px-4 py-3 text-gray-700">{task.processingItems}</td>
                        <td className="px-4 py-3 text-gray-700">{task.failedItems}</td>
                        <td className="px-4 py-3 text-gray-700">{task.skippedItems}</td>
                        <td className="px-4 py-3 text-gray-700">
                          {Number.isFinite(task.processedTokens) ? task.processedTokens.toLocaleString() : '-'}
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          {Number.isFinite(task.processedChunks) ? task.processedChunks.toLocaleString() : '-'}
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          {new Date(task.createdAt).toLocaleString()}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => router.push(`/knowledge-base/sync-tasks/${task.id}`)}
                            >
                              {t.knowledgeBase.syncTaskList.table.view}
                            </Button>
                            {task.status === 'SYNCING' && (
                              <Button
                                variant="destructive"
                                size="sm"
                                className="gap-1"
                                disabled={terminatingTaskId === task.id}
                                onClick={() => handleTerminate(task)}
                              >
                                {terminatingTaskId === task.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Ban className="h-4 w-4" />
                                )}
                                {t.knowledgeBase.syncTaskList.table.terminate}
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
